import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Collapse,
  Chip,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import useMatchPhoto from "./useMatchPhoto";

const SUCCESS_MS = 1500;
const MESSAGE_MS = 3200;

const PLACEHOLDER_GRADIENTS = [
  "linear-gradient(160deg, #c86b86 0%, #7a2f45 100%)",
  "linear-gradient(160deg, #c99b6e 0%, #8a5a34 100%)",
  "linear-gradient(160deg, #7e8bbd 0%, #3f4a75 100%)",
  "linear-gradient(160deg, #6fae94 0%, #34614a 100%)",
];

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : null;

const firstNameOf = (name) => (name ? name.split(" ")[0] : "They");

const PhotoBlock = ({ match, index }) => {
  const photo = useMatchPhoto(match);
  if (photo) {
    return (
      <Box
        sx={{
          height: 260,
          backgroundImage: `url(${photo.url})`,
          backgroundSize: "cover",
          backgroundPosition: `${photo.focalX}% ${photo.focalY}%`,
        }}
      />
    );
  }
  return (
    <Box
      sx={{
        height: 260,
        background: PLACEHOLDER_GRADIENTS[index % PLACEHOLDER_GRADIENTS.length],
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      <PersonIcon sx={{ fontSize: 200, color: "rgba(0,0,0,0.22)", mb: -2 }} />
    </Box>
  );
};

const StatusNote = ({ icon, children }) => (
  <Box
    sx={{
      display: "flex",
      gap: 1,
      alignItems: "flex-start",
      p: 1.5,
      borderRadius: 2,
      bgcolor: "rgba(46,155,91,0.10)",
    }}
  >
    <Box sx={{ color: "#2e9b5b", mt: "1px", flexShrink: 0 }}>{icon}</Box>
    <Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.5 }}>
      {children}
    </Typography>
  </Box>
);

const SentMessage = ({ name }) => (
  <Box sx={{ mb: 2.5 }}>
    <StatusNote icon={<CheckCircleOutlineIcon sx={{ fontSize: 18 }} />}>
      <b>Interest sent.</b> If {firstNameOf(name)} feels the same, we will make
      the introduction over email.
    </StatusNote>
  </Box>
);

const SuccessOverlay = () => (
  <Box
    sx={{
      position: "absolute",
      inset: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      bgcolor: "rgba(255,255,255,0.9)",
      zIndex: 2,
    }}
  >
    <FavoriteIcon
      sx={{
        color: "primary.main",
        fontSize: 68,
        animation: "mmPop 600ms cubic-bezier(.17,.67,.34,1.53)",
        "@keyframes mmPop": {
          "0%": { transform: "scale(0)", opacity: 0 },
          "60%": { transform: "scale(1.15)", opacity: 1 },
          "100%": { transform: "scale(1)", opacity: 1 },
        },
      }}
    />
    <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.main" }}>
      Interest sent
    </Typography>
  </Box>
);

const MatchCard = ({
  match,
  index,
  busy,
  success,
  onPass,
  onInterested,
  onFeedback,
}) => {
  const name = match.otherName || "Your match";
  const date = formatDate(match.matchedAt);

  let action;
  if (match.connected) {
    action = (
      <>
        <StatusNote icon={<FavoriteIcon sx={{ fontSize: 18 }} />}>
          <b>It's a match!</b> You both said yes. Look out for an introduction
          over email.
        </StatusNote>
        {match.feedbackEligible ? (
          <FormControl fullWidth size="small" sx={{ mt: 1.75 }}>
            <InputLabel id={`fb-${match.id}`}>
              Have you been in touch?
            </InputLabel>
            <Select
              labelId={`fb-${match.id}`}
              value={match.feedback || ""}
              label="Have you been in touch?"
              onChange={(e) => onFeedback(match.id, e.target.value)}
            >
              <MenuItem value="inContact">In contact</MenuItem>
              <MenuItem value="noResponse">No response</MenuItem>
            </Select>
            <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
              Your response is private and never shared with {firstNameOf(name)}.
            </Typography>
          </FormControl>
        ) : null}
      </>
    );
  } else if (match.youHearted) {
    action = <SentMessage name={name} />;
  } else {
    action = (
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button
          className="tnfm-pill-btn"
          variant="outlined"
          fullWidth
          disabled={busy}
          startIcon={<CloseIcon />}
          onClick={() => onPass(match.id)}
        >
          Pass
        </Button>
        <Button
          className="tnfm-pill-btn"
          variant="contained"
          fullWidth
          disabled={busy}
          startIcon={<FavoriteIcon />}
          onClick={() => onInterested(match.id)}
        >
          Interested
        </Button>
      </Box>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 3, overflow: "hidden", mb: 2.5, position: "relative" }}
    >
      {success ? <SuccessOverlay /> : null}
      <PhotoBlock match={match} index={index} />
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{name}</Typography>
          {match.connected ? (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.4,
                borderRadius: 5,
                bgcolor: "rgba(46,155,91,0.14)",
                color: "#2e9b5b",
                flexShrink: 0,
              }}
            >
              <FavoriteIcon sx={{ fontSize: 12 }} />
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                It's a match
              </Typography>
            </Box>
          ) : date ? (
            <Typography
              sx={{
                fontSize: 11,
                letterSpacing: 0.5,
                color: "text.secondary",
                textTransform: "uppercase",
                flexShrink: 0,
              }}
            >
              {`Matched ${date}`}
            </Typography>
          ) : null}
        </Box>

        {match.blurb ? (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mt: 1,
                mb: 0.75,
                color: "primary.main",
              }}
            >
              <FavoriteBorderIcon sx={{ fontSize: 13 }} />
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                Why your matchmaker picked them
              </Typography>
            </Box>
            <Typography
              sx={{ fontSize: 14, color: "text.primary", lineHeight: 1.55, mb: 2 }}
            >
              {match.blurb}
            </Typography>
          </>
        ) : (
          <Box sx={{ mb: 2 }} />
        )}

        {action}
      </Box>
    </Paper>
  );
};

const MatchItem = ({
  match,
  index,
  busy,
  phase,
  onPass,
  onInterested,
  onFeedback,
  onCardExited,
  onMessageExited,
}) => {
  const showCard = phase == null || phase === "success";
  const showMessage = phase === "message";

  return (
    <Box>
      <Collapse in={showCard} timeout={350} onExited={onCardExited}>
        <MatchCard
          match={match}
          index={index}
          busy={busy || phase != null}
          success={phase === "success"}
          onPass={onPass}
          onInterested={onInterested}
          onFeedback={onFeedback}
        />
      </Collapse>
      <Collapse in={showMessage} timeout={350} onExited={onMessageExited}>
        <SentMessage name={match.otherName} />
      </Collapse>
    </Box>
  );
};

const InterestConfirmDialog = ({
  open,
  busy,
  dontShowAgain,
  onToggleDontShowAgain,
  onCancel,
  onConfirm,
}) => (
  <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
    <DialogContent>
      <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 1.5 }}>
        Before you say you're interested
      </Typography>
      <Typography sx={{ fontSize: 14, color: "text.secondary", lineHeight: 1.55, mb: 1.25 }}>
        Matchmaker is built on intention. You showed up to a real event, and you
        took the time to tell us who you are and who you're hoping to meet. That
        effort is what makes this different from endless swiping.
      </Typography>
      <Typography sx={{ fontSize: 14, color: "text.secondary", lineHeight: 1.55, mb: 1.25 }}>
        If you say you're interested and your match says the same, we'll
        introduce the two of you over email, and we trust you to follow through.
      </Typography>
      <Typography sx={{ fontSize: 14, color: "text.secondary", lineHeight: 1.55, mb: 1.25 }}>
        After an introduction, each of you can tell us if the other person
        ghosted. If we receive a report and verify it, that person is removed
        from Matchmaker permanently.
      </Typography>
      <Typography sx={{ fontSize: 14, color: "text.primary", fontWeight: 600, lineHeight: 1.55 }}>
        Only continue if you're ready to take this seriously.
      </Typography>
      <FormControlLabel
        sx={{ mt: 1.5 }}
        control={
          <Checkbox
            checked={dontShowAgain}
            onChange={(e) => onToggleDontShowAgain(e.target.checked)}
            disabled={busy}
            size="small"
          />
        }
        label={
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            Don't show this again
          </Typography>
        }
      />
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button
        className="tnfm-pill-btn"
        onClick={onCancel}
        disabled={busy}
        color="inherit"
      >
        Cancel
      </Button>
      <Button
        className="tnfm-pill-btn"
        onClick={onConfirm}
        disabled={busy}
        variant="contained"
      >
        Yes, I'm interested
      </Button>
    </DialogActions>
  </Dialog>
);

const MatchesView = ({
  matches,
  loading,
  busyId,
  interestAck,
  onBack,
  onPass,
  onInterested,
  onAcknowledge,
  onDismiss,
  onFeedback,
}) => {
  const [confirmId, setConfirmId] = React.useState(null);
  const [acking, setAcking] = React.useState(false);
  const [dontShowAgain, setDontShowAgain] = React.useState(false);
  const [anim, setAnim] = React.useState({});
  const [filter, setFilter] = React.useState("new");
  const didInit = React.useRef(false);
  const active = matches || [];

  const setPhase = (id, phase) =>
    setAnim((s) => {
      if (phase == null) {
        const next = { ...s };
        delete next[id];
        return next;
      }
      return { ...s, [id]: phase };
    });

  const beginInterest = async (id) => {
    setPhase(id, "success");
    const res = await onInterested(id);
    if (!res || !res.success || res.connected) {
      setPhase(id, null);
      return;
    }
    window.setTimeout(() => setPhase(id, "winking"), SUCCESS_MS);
  };

  const handleCardExited = (id) => {
    setAnim((s) => (s[id] === "winking" ? { ...s, [id]: "message" } : s));
    window.setTimeout(
      () => setAnim((s) => (s[id] === "message" ? { ...s, [id]: "done" } : s)),
      MESSAGE_MS,
    );
  };

  const handleMessageExited = (id) => {
    setPhase(id, null);
    if (onDismiss) onDismiss(id);
  };

  const requestInterest = (id) => {
    if (interestAck) {
      beginInterest(id);
    } else {
      setDontShowAgain(false);
      setConfirmId(id);
    }
  };

  const confirmInterest = async () => {
    const id = confirmId;
    if (dontShowAgain) {
      setAcking(true);
      await onAcknowledge();
      setAcking(false);
    }
    setConfirmId(null);
    if (id) beginInterest(id);
  };

  const newMatches = active.filter((m) => !m.connected);
  const mutualMatches = active.filter((m) => m.connected);
  const list = filter === "mutual" ? mutualMatches : newMatches;

  React.useEffect(() => {
    if (didInit.current || active.length === 0) return;
    didInit.current = true;
    const hasNew = active.some((m) => !m.connected);
    const hasMutual = active.some((m) => m.connected);
    if (!hasNew && hasMutual) setFilter("mutual");
  }, [active]);

  let heading;
  let headingAccent;
  let accentColor = "primary.main";
  let subtitle;
  if (filter === "mutual") {
    accentColor = "#2e9b5b";
    if (mutualMatches.length === 0) {
      heading = "No matches yet.";
      headingAccent = "";
    } else {
      heading = `${mutualMatches.length} `;
      headingAccent =
        mutualMatches.length === 1 ? "mutual match." : "mutual matches.";
    }
    subtitle = "You both said yes. We'll introduce you over email.";
  } else {
    if (newMatches.length === 0) {
      heading = "You're all caught up.";
      headingAccent = "";
    } else {
      heading = `${newMatches.length} `;
      headingAccent =
        newMatches.length === 1
          ? "person worth meeting."
          : "people worth meeting.";
    }
    subtitle =
      "Picked by your matchmaker from people who have come to our events. Respond below; nothing is shared until it is mutual.";
  }

  return (
    <Box sx={{ p: 2.5, pb: 12, maxWidth: 560, mx: "auto" }}>
      <Typography
        onClick={onBack}
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: "primary.main",
          cursor: "pointer",
          mb: 2,
        }}
      >
        {"‹ Matchmaker"}
      </Typography>

      {active.length > 0 ? (
        <Box sx={{ display: "flex", gap: 1, mb: 2.5 }}>
          <Chip
            label={`New (${newMatches.length})`}
            onClick={() => setFilter("new")}
            color={filter === "new" ? "primary" : "default"}
            variant={filter === "new" ? "filled" : "outlined"}
            sx={{ fontWeight: 600 }}
          />
          <Chip
            label={`Mutual (${mutualMatches.length})`}
            onClick={() => setFilter("mutual")}
            color={filter === "mutual" ? "success" : "default"}
            variant={filter === "mutual" ? "filled" : "outlined"}
            sx={{ fontWeight: 600 }}
          />
        </Box>
      ) : null}

      <Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.2 }}>
        {heading}
        <Box component="span" sx={{ color: accentColor }}>
          {headingAccent}
        </Box>
      </Typography>
      <Typography
        sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.5, mt: 1, mb: 3 }}
      >
        {subtitle}
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : list.length === 0 ? (
        <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
          {filter === "mutual"
            ? "No mutual matches yet. When you and a match both say yes, they'll show up here."
            : "No new matches to review right now. We'll let you know when there's someone new."}
        </Typography>
      ) : (
        list.map((m, i) => (
          <MatchItem
            key={m.id}
            match={m}
            index={i}
            busy={busyId === m.id}
            phase={anim[m.id]}
            onPass={onPass}
            onInterested={requestInterest}
            onFeedback={onFeedback}
            onCardExited={() => handleCardExited(m.id)}
            onMessageExited={() => handleMessageExited(m.id)}
          />
        ))
      )}

      <InterestConfirmDialog
        open={!!confirmId}
        busy={acking}
        dontShowAgain={dontShowAgain}
        onToggleDontShowAgain={setDontShowAgain}
        onCancel={() => setConfirmId(null)}
        onConfirm={confirmInterest}
      />
    </Box>
  );
};

export default MatchesView;
